import List from '@e3dc-react/shell/Elements/List/List'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { PvInverterModel } from 'src/Libs/solar/models/solar.model'

interface PvInvertersTableProps {
  pvInverters: PvInverterModel[]
}

/**
 * Table to display all pv inverters for a storage
 * currently only one inverter is possible, but this will change in the future
 */
const PvInvertersTable: React.FC<PvInvertersTableProps> = ({ pvInverters }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const showListView = useMediaQuery(theme.breakpoints.down('sm'))

  const powermeterTableColumns: Header<PvInverterModel>[] = [
    {
      label: t('common.id'),
      value: 'id',
      valueRender: (_, index) => {
        return t('system-details.components.pv-inverters.prefixed-id', { id: index })
      },
      isSortable: true,
      sortFunction: nullsLast((a: number, b: number) => a - b),
    },
    {
      value: 'type',
      label: t('common.type'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => value ?? '-',
    },
    {
      value: 'serialnumber',
      label: t('system-details.components.pv-inverters.assembly-number'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => value ?? '-',
    },
    {
      value: 'version',
      label: t('common.softwareVersion'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => value ?? '-',
    },
    {
      value: 'picVersion',
      label: t('system-details.components.pv-inverters.pic-version'),
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => value ?? '-',
    },
  ]

  if (showListView) {
    return (
      <Box sx={theme => ({ paddingBottom: theme.spacing(2) })}>
        <List
          data={pvInverters.map((item, index) => ({
            primary: t('system-details.components.pv-inverters.prefixed-id', { id: index }),
            secondary: item.type,
          }))}
          noDataText={t('system-details.components.pv-inverters.no-data')}
        />
      </Box>
    )
  }

  return (
    <TableComplete
      header={powermeterTableColumns}
      data={pvInverters ?? []}
      headerElevation={0}
      bodyElevation={0}
      footerElevation={0}
      coloredColumn
      disablePagination
      noDataText={t('system-details.components.pv-inverters.no-data')}
    />
  )
}

export default PvInvertersTable
